'use client';

import * as React from 'react';
import Link from 'next/link';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  CheckCircle2Icon,
  MessageSquareIcon,
  MicIcon,
  ShieldCheckIcon,
  WalletCardsIcon,
  WorkflowIcon
} from 'lucide-react';

import {
  APP_CONTACT_EMAIL,
  APP_DESCRIPTION,
  APP_DISCORD_INVITE_URL,
  APP_GITHUB_REPO_URL,
  APP_INSTALL_FALLBACK_COMMAND,
  APP_INSTALL_SCRIPT_COMMAND,
  APP_NAME
} from '@workspace/common/app';
import { baseUrl, routes } from '@workspace/routes';
import { buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

import { CommunityConnectCard } from '~/components/community-connect-card';
import { GridSection } from '~/components/fragments/grid-section';
import { JsonLd } from '~/components/fragments/json-ld';
import { ViventiumIndexV2Hero } from '~/components/sections/command-cortex-centered-lab';
import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';

const REASONS: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: 'Better decisions',
    description:
      'Important choices should not depend on one model with one bias. Viventium can bring in specialized agents to challenge assumptions and add perspective behind the main conversation.',
    icon: CheckCircle2Icon
  },
  {
    title: 'Lower cost',
    description:
      'Use the OpenAI or Anthropic account you already pay for instead of getting trapped in a whole new API billing silo.',
    icon: WalletCardsIcon
  },
  {
    title: 'More trust',
    description:
      'Run local-first, inspect the code, switch providers, and keep more of your private context under your own control.',
    icon: ShieldCheckIcon
  }
];

const CAPABILITIES: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    title: 'Voice, chat, and messaging',
    description:
      'Keep one system across talking, typing, and texting when different moments call for different interfaces.',
    icon: MicIcon
  },
  {
    title: 'Background agents',
    description:
      'The first reply can stay fast while deeper research, bias checks, or memory work continue only when they are useful.',
    icon: MessageSquareIcon
  },
  {
    title: 'Scheduling',
    description:
      'Set reminders, recurring prompts, and follow-through without turning your life into notification spam.',
    icon: CalendarDaysIcon
  },
  {
    title: 'Workers and projects',
    description:
      'Turn one AI into a team that can keep working inside safer sandboxes while you sleep.',
    icon: WorkflowIcon
  }
];

const SUPPORT_LINKS = [
  {
    title: 'Docs',
    href: routes.marketing.Docs,
    external: false
  },
  {
    title: 'Changelog',
    href: routes.marketing.Changelog,
    external: false
  },
  {
    title: 'Roadmap',
    href: routes.marketing.Roadmap,
    external: false
  }
] as const;

const VIVENTIUM_DEMO_VIDEO_ID = 'nTCYGFW7Hcw';
const VIVENTIUM_DEMO_VIDEO_URL = `https://www.youtube.com/watch?v=${VIVENTIUM_DEMO_VIDEO_ID}`;
const VIVENTIUM_DEMO_EMBED_URL = `https://www.youtube-nocookie.com/embed/${VIVENTIUM_DEMO_VIDEO_ID}`;

const HOME_STRUCTURED_DATA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseUrl.Marketing}#organization`,
    name: APP_NAME,
    url: baseUrl.Marketing,
    description: APP_DESCRIPTION,
    logo: `${baseUrl.Marketing}/apple-touch-icon.png`,
    email: APP_CONTACT_EMAIL,
    sameAs: [
      APP_GITHUB_REPO_URL,
      APP_DISCORD_INVITE_URL,
      'https://www.youtube.com/@adrienbeyk',
      'https://x.com/AdrienBeyk',
      'https://www.linkedin.com/in/adrienbeyk',
      'https://www.linkedin.com/company/viventiumai/',
      'https://t.me/viventiumai'
    ]
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${baseUrl.Marketing}#website`,
    name: APP_NAME,
    url: baseUrl.Marketing,
    description: APP_DESCRIPTION,
    inLanguage: 'en-US',
    publisher: {
      '@id': `${baseUrl.Marketing}#organization`
    }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${baseUrl.Marketing}#webpage`,
    name: `${APP_NAME} | Stop relying on one AI for decisions that matter.`,
    url: baseUrl.Marketing,
    description: APP_DESCRIPTION,
    isPartOf: {
      '@id': `${baseUrl.Marketing}#website`
    },
    about: {
      '@id': `${baseUrl.Marketing}#organization`
    }
  }
];

function CopyCommandButton({
  command,
  onCopy
}: {
  command: string;
  onCopy?: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = React.useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command);
      onCopy?.();
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        buttonVariants({ variant: 'outline', size: 'sm' }),
        'h-8 rounded-full px-3 text-xs'
      )}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function CommandCard({
  eyebrow,
  command,
  description,
  onCopy
}: {
  eyebrow: string;
  command: string;
  description: string;
  onCopy?: () => void;
}): React.JSX.Element {
  return (
    <div className="min-w-0 rounded-3xl border border-border/70 bg-background p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {eyebrow}
        </p>
        <CopyCommandButton
          command={command}
          onCopy={onCopy}
        />
      </div>
      <pre className="mt-4 max-w-full overflow-x-auto rounded-2xl bg-muted/40 p-4 text-sm leading-7 text-foreground">
        <code className="block whitespace-pre-wrap break-all">{command}</code>
      </pre>
      <p className="mt-3 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

export default function IndexV2Page(): React.JSX.Element {
  const { trackDiscordClick, trackDocsClick, trackInstallClick } =
    useMarketingAnalytics();

  return (
    <main className="overflow-x-clip">
      <JsonLd data={HOME_STRUCTURED_DATA} />
      <ViventiumIndexV2Hero />

      <GridSection id="community">
        <div className="container py-12 md:py-14">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-center">
            <div className="max-w-xl space-y-4">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Stay close to the build
              </p>
              <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
                Join the public conversation. Get email for the big updates.
              </h2>
              <p className="text-base leading-7 text-muted-foreground sm:text-lg">
                Community channels are for the day-to-day build. Email is for
                major releases, docs changes, and public milestones.
              </p>
            </div>

            <CommunityConnectCard />
          </div>
        </div>
      </GridSection>

      <GridSection id="features">
        <div className="container py-20 md:py-24">
          <div className="max-w-2xl space-y-4">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Why people switch
            </p>
            <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
              One chatbot is easy. Better judgment is harder.
            </h2>
            <p className="text-lg text-muted-foreground">
              When the answer matters, perspective, cost, and trust matter too.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REASONS.map((reason) => {
              const Icon = reason.icon;

              return (
                <div
                  key={reason.title}
                  className="rounded-3xl border border-border/70 bg-background p-6 shadow-sm"
                >
                  <div className="flex size-11 items-center justify-center rounded-2xl bg-muted/60">
                    <Icon className="size-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold">{reason.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-muted-foreground">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-6 overflow-hidden rounded-3xl border border-border/70 bg-background shadow-sm">
            <div className="grid gap-0 lg:grid-cols-[minmax(0,0.85fr)_minmax(340px,1.15fr)]">
              <div className="flex flex-col justify-between gap-6 p-6 md:p-8">
                <div>
                  <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
                    Video demo
                  </p>
                  <h3 className="mt-4 max-w-xl text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
                    See Viventium as a working second brain.
                  </h3>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground">
                    Adrien Beyk (@AdrienBeyk) walks through the product in
                    motion, so the brain-inspired concept connects to the actual
                    experience.
                  </p>
                </div>
                <a
                  href={VIVENTIUM_DEMO_VIDEO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-foreground underline-offset-4 transition hover:underline"
                >
                  Watch on YouTube
                </a>
              </div>
              <div className="relative aspect-video min-h-[220px] bg-muted/40 lg:aspect-auto">
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={VIVENTIUM_DEMO_EMBED_URL}
                  title="DEMO | This AI Wants to be Your Second Brain: Viventium"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </GridSection>

      <GridSection>
        <div className="container py-20 md:py-24">
          <div className="max-w-2xl space-y-4">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Beyond chat
            </p>
            <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
              Think with it. Talk to it. Let it follow through.
            </h2>
            <p className="text-lg text-muted-foreground">
              Viventium starts as a conversation, but it does not stop there.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {CAPABILITIES.map((capability) => {
              const Icon = capability.icon;

              return (
                <div
                  key={capability.title}
                  className="rounded-3xl border border-border/70 bg-muted/20 p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-11 items-center justify-center rounded-2xl bg-background shadow-sm">
                      <Icon className="size-5" />
                    </div>
                    <h3 className="text-lg font-semibold">
                      {capability.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm leading-7 text-muted-foreground">
                    {capability.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </GridSection>

      <GridSection id="install">
        <div className="container py-20 md:py-24">
          <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
            <div className="min-w-0 space-y-5">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Install
              </p>
              <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                Install first. Learn deeper when you need it.
              </h2>
              <p className="text-lg text-muted-foreground">
                The fastest path is the hosted installer. If you want to inspect
                the repo directly, the fallback path is right below.
              </p>
              <div className="divide-y divide-border/70 rounded-3xl border border-border/70 bg-background">
                <div className="p-5">
                  <p className="font-semibold text-foreground">
                    Have a Groq key ready
                  </p>
                  <p className="mt-2 text-sm leading-7 text-muted-foreground">
                    Groq is the default fast, low-cost path for deciding whether
                    a background agent should wake up.
                  </p>
                </div>
                <div className="p-5">
                  <p className="font-semibold text-foreground">
                    Connect your main account
                  </p>
                  <p className="mt-2 text-sm leading-7 text-muted-foreground">
                    After launch, open{' '}
                    <code>Settings -&gt; Connected Accounts</code> and connect
                    your OpenAI or Anthropic account.
                  </p>
                </div>
                <div className="p-5">
                  <p className="font-semibold text-foreground">
                    Need the full setup path?
                  </p>
                  <p className="mt-2 text-sm leading-7 text-muted-foreground">
                    The docs cover installation, configuration, and what to
                    expect on first run.
                  </p>
                  <Link
                    href="/docs/getting-started"
                    onClick={() =>
                      trackDocsClick(
                        'install_section_docs',
                        `${baseUrl.Marketing}/docs/getting-started`
                      )
                    }
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-primary"
                  >
                    Read the docs
                    <ArrowRightIcon className="size-4 shrink-0" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="min-w-0 space-y-4">
              <CommandCard
                eyebrow="Fastest path"
                command={APP_INSTALL_SCRIPT_COMMAND}
                description="Hosted installer for quick local setup."
                onCopy={() =>
                  trackInstallClick(
                    'install_section_fastest_path',
                    'copy_command',
                    APP_INSTALL_SCRIPT_COMMAND
                  )
                }
              />
              <CommandCard
                eyebrow="Fallback"
                command={APP_INSTALL_FALLBACK_COMMAND}
                description="Use the repo-local install path directly if you prefer to inspect everything step by step."
                onCopy={() =>
                  trackInstallClick(
                    'install_section_fallback_path',
                    'copy_command',
                    APP_INSTALL_FALLBACK_COMMAND
                  )
                }
              />
            </div>
          </div>
        </div>
      </GridSection>

      <GridSection hideBottomGridLine>
        <div className="container py-20 md:py-24">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] lg:items-start">
            <div className="max-w-2xl space-y-5">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Belief
              </p>
              <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
                Built in public for people who need better judgment, not just
                faster answers.
              </h2>
              <p className="text-lg text-muted-foreground">
                Viventium exists because people should not have to trust one
                hidden AI voice with the decisions that matter most.
              </p>
              <p className="text-sm leading-7 text-muted-foreground">
                The project is being shaped in public so normal people can get
                the benefit of specialized AI systems without babysitting a
                brittle stack or locking themselves into one provider.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link
                  href={routes.marketing.Story}
                  className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'rounded-xl'
                  )}
                >
                  Read the story
                </Link>
                <a
                  href={APP_DISCORD_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDiscordClick('belief_section_discord')}
                  className={cn(
                    buttonVariants({ variant: 'ghost' }),
                    'rounded-xl'
                  )}
                >
                  Join Discord
                </a>
              </div>
            </div>

            <div className="space-y-5 rounded-[2rem] border border-border/70 bg-background p-6 shadow-sm">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Keep exploring
                </p>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">
                  The docs explain how it works. The changelog shows what is
                  real today. The roadmap shows what is next.
                </p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
                {SUPPORT_LINKS.map((link) =>
                  link.external ? (
                    <a
                      key={link.title}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {link.title}
                      <ArrowRightIcon className="size-4 shrink-0" />
                    </a>
                  ) : (
                    <Link
                      key={link.title}
                      href={link.href}
                      onClick={() => {
                        if (link.title === 'Docs') {
                          trackDocsClick('home_keep_exploring_docs', link.href);
                        }
                      }}
                      className="inline-flex items-center gap-2 font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {link.title}
                      <ArrowRightIcon className="size-4 shrink-0" />
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </GridSection>
    </main>
  );
}
